import React, { lazy, Suspense } from 'react';

import { AppLoading } from 'components/AppLoading';
import { MenuItem, isFormExtras, isMeetingRoomExtras } from 'types/menuTypes';

// ===================================================================
// Centralized Component Registry
// ===================================================================
// Single source of truth for mapping dynamic_component values to
// lazy-loaded React components. Used by both RenderMenuContent and
// ThirdLevelLayout to eliminate duplicated switch/case blocks.
// ===================================================================

const CommunityProfile = lazy(() =>
  import('pages/CommunityProfile').then(module => ({ default: module.CommunityProfile }))
);
const FormListPage = lazy(() => import('pages/FormListPage'));
const FormsDashboard = lazy(() =>
  import('pages/FormsDashboard').then(module => ({ default: module.FormsDashboard }))
);
const NetworkDashboard = lazy(() =>
  import('pages/NetworkDashboard').then(module => ({ default: module.NetworkDashboard }))
);
const Rooms = lazy(() => import('pages/Rooms'));
const UnderConstruction = lazy(() =>
  import('components/UnderConstruction').then(module => ({ default: module.UnderConstruction }))
);

interface RenderDynamicComponentProps {
  menu: MenuItem;
  children?: React.ReactNode;
  /** Optional index for Rooms component */
  index?: number;
  /** Whether to wrap in Suspense (default: true) */
  withSuspense?: boolean;
}

/**
 * Renders the correct component based on menu.feature.dynamic_component.
 * Single source of truth - used by both RenderMenuContent and ThirdLevelLayout.
 */
export const renderDynamicComponent = ({
  menu,
  children,
  index,
  withSuspense = true,
}: RenderDynamicComponentProps): React.ReactNode => {
  const dynamicComponent = menu.feature?.dynamic_component;
  const extras = menu.extras || menu.feature?.extras || null;

  const wrap = (node: React.ReactNode) =>
    withSuspense ? <Suspense fallback={<AppLoading />}>{node}</Suspense> : node;

  switch (dynamicComponent) {
    case 'FORM':
      if (isFormExtras(extras)) {
        return wrap(
          <FormListPage
            formGroupId={extras.form_group_id}
            formGroupName={extras.form_group_name}
            menu={menu}
          />
        );
      }
      return wrap(<FormListPage menu={menu} />);

    case 'FORM_DYNAMIC':
      if (isFormExtras(extras)) {
        return wrap(
          <FormListPage
            key={extras.form_group_id}
            formGroupId={extras.form_group_id}
            formGroupName={extras.form_group_name}
            menu={menu}
          />
        );
      }
      return wrap(<FormListPage key={menu.label} menu={menu} />);

    case 'FORM_DASHBOARD':
      if (isFormExtras(extras)) {
        return wrap(
          <CommunityProfile formId={extras.form_id} formName={extras.form_name} menu={menu} />
        );
      }
      return wrap(<CommunityProfile menu={menu} />);

    case 'FORMS_DASHBOARD':
      if (isFormExtras(extras)) {
        return wrap(
          <FormsDashboard formId={extras.form_id} formName={extras.form_name} menu={menu} />
        );
      }
      return wrap(<FormsDashboard menu={menu} />);

    case 'NETWORK_DASHBOARD':
      return wrap(<NetworkDashboard menu={menu} />);

    case 'MEETING_ROOM':
    case 'MEETING_ROOM_ROOM':
      if (isMeetingRoomExtras(extras)) {
        return wrap(
          <Rooms
            roomId={extras.internal_id}
            roomName={menu.label}
            vendorCode={extras.vendor_code}
            roomKey={extras.key}
            background={extras.background}
            menu={menu}
            index={index}
          />
        );
      }
      return wrap(<Rooms menu={menu} index={index} />);

    default:
      return children != null ? (
        <div className="w-full">{children}</div>
      ) : (
        wrap(<UnderConstruction />)
      );
  }
};
